import { ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";

interface FormSubBlockProps {
  /** Sem título, o bloco só separa — é o caso do bloco de campo único, cujo rótulo já nomeia. */
  title?: string;
  children: ReactNode;
}

/**
 * Mini-bloco dentro de uma seção: rótulo (eyebrow) + conteúdo. Fatiar uma seção
 * pesada em sub-grupos ("Identificação", "Classificação"…) ajuda o usuário a
 * digerir formulários longos. Compartilhado entre criação e edição.
 *
 * A separação entre blocos vem de uma hairline no topo (removida no primeiro via
 * `first:`) — por isso os blocos devem ser **filhos diretos** do container, sem
 * `space-y` próprio.
 */
export function FormSubBlock({ title, children }: FormSubBlockProps) {
  const prefersReducedMotion = useReducedMotion();

  return (
    // motion.section preserva o elemento <section> (e os seletores `first:`).
    // Entrada sutil de fade + leve subida ao montar; anulada em reduced-motion.
    <motion.section
      initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, ease: "easeOut" }}
      className="mt-8 space-y-6 border-t border-border pt-8 first:mt-0 first:border-t-0 first:pt-0"
    >
      {title && (
        <span className="block text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {title}
        </span>
      )}
      {children}
    </motion.section>
  );
}
